import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, Injectable, Output, inject } from '@angular/core';
import { HttpHelpersService, LoadingService } from '@coin/shared/core/data-access';
import { environment } from '@coin/shared/core/util-environments';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, retryWhen, switchMap, tap } from 'rxjs/operators';
import {
  PaginatedResult,
  RuleSetV2,
  SimpleTableService,
  TransactionStatus,
  TransactionStatusMetadata
} from '@coin/shared/core/util-models';
import { checkResult, pollingStrategy, RuleEngineHelper } from '@coin/shared/core/util-helpers';
import { AllowedAddress, CommunicationModel, TagModel } from '@coin/shared/communication/util';
import { RuleEngineV2Service } from '@coin/shared/core/data-access';

@Injectable({
  providedIn: 'root'
})
export class CommunicationService implements SimpleTableService {
  private readonly loadingService = inject(LoadingService);
  private readonly httpClient = inject(HttpClient);
  private readonly toast = inject(ToastrService);
  private readonly ruleSet = inject(RuleEngineV2Service);
  private readonly httpHelper = inject(HttpHelpersService);

  @Output() toggleReload = new EventEmitter<void>();

  public queryPagedAndFilteredData = this.getPaginatedAndFilteredCommunications;

  private getPaginatedAndFilteredCommunications(
    page: number,
    size: number,
    searchText?: string,
    filterText?: string,
    sortText?: string
  ): Observable<PaginatedResult<CommunicationModel>> {
    return this.httpClient
      .get<
        PaginatedResult<CommunicationModel>
      >(`${environment.api.baseUrl}/admin/api/v1/master/communications?Paging.Page=${page + 1}&Paging.Size=${size}${filterText}${sortText}`)
      .pipe(
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error retrieving season data. ${res?.error?.reason || 'Unknown'} `);
          return throwError(`Error retrieving season data. ${res?.error?.reason || 'Unknown'} `);
        })
      );
  }

  public getFieldValues(
    page: number,
    size: number,
    targetProperty: string,
    seasonId: string,
    searchText?: string
  ): Observable<PaginatedResult<string>> {
    const searchQuery = searchText.trim() ? `&${targetProperty}=${encodeURIComponent(searchText)}` : '';

    return this.httpClient
      .get<
        PaginatedResult<string>
      >(`${environment.api.baseUrl}/admin/api/v1/master/communications/field-values?Page=${page + 1}&Size=${size}&targetProperty=${targetProperty}${searchQuery}`)
      .pipe(catchError(() => of(null)));
  }

  // NOTIFICATIONS
  public getNotification(id: string): Observable<CommunicationModel> {
    return this.httpClient
      .get<CommunicationModel>(`${environment.api.baseUrl}/admin/api/v1/master/communications/notifications/${id}`)
      .pipe(
        map(checkResult()),
        retryWhen(pollingStrategy()),
        switchMap(result => this.addRuleSet(result)),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error retrieving task. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error retrieving task data. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public createNotification(body: CommunicationModel): Observable<unknown> {
    return this.httpClient
      .post(`${environment.api.baseUrl}/admin/api/v1/master/communications/notifications`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        tap(() => this.toggleReload.emit()),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error creating task. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error creating task. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public updateNotificationSettings(id: string, body: CommunicationModel): Observable<unknown> {
    return this.httpClient
      .patch(`${environment.api.baseUrl}/admin/api/v1/master/communications/notifications/${id}/settings`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error updating task settings. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error updating task settings. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public updateNotificationRuleSet(id: string, body: CommunicationModel): Observable<unknown> {
    body.ruleSet = RuleEngineHelper.ruleSetV2ToDto(body.ruleSet);
    return this.httpClient
      .patch(`${environment.api.baseUrl}/admin/api/v1/master/communications/notifications/${id}/rule-set`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error updating task target group. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error updating task target group. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public updateNotificationMessage(id: string, body: CommunicationModel): Observable<unknown> {
    return this.httpClient
      .patch(`${environment.api.baseUrl}/admin/api/v1/master/communications/notifications/${id}/message`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error updating task message. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error updating task message. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public publishNotification(
    id: string,
    body: CommunicationModel
  ): Observable<TransactionStatus<TransactionStatusMetadata>> {
    return this.httpClient
      .patch<
        TransactionStatus<TransactionStatusMetadata>
      >(`${environment.api.baseUrl}/admin/api/v1/master/communications/notifications/${id}/publish`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error publishing task. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error publishing task. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public archiveNotification(id: string, body: CommunicationModel): Observable<unknown> {
    return this.httpClient
      .patch(`${environment.api.baseUrl}/admin/api/v1/master/communications/notifications/${id}/archive`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error archiving task. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error archiving task. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public deleteNotificationLabels(id: string, body: TagModel): Observable<unknown> {
    return this.httpClient
      .request('delete', `${environment.api.baseUrl}/admin/api/v1/master/communications/notifications/${id}/labels`, {
        body
      })
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`${res?.error?.message}: ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error deleting labels. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  /* TASK */
  public getTask(id: string): Observable<CommunicationModel> {
    return this.httpClient
      .get<CommunicationModel>(`${environment.api.baseUrl}/admin/api/v1/master/communications/tasks/${id}`)
      .pipe(
        map(checkResult()),
        retryWhen(pollingStrategy()),
        switchMap(result => this.addRuleSet(result)),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error retrieving task. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error retrieving task data. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public createTask(body: CommunicationModel): Observable<unknown> {
    return this.httpClient.post(`${environment.api.baseUrl}/admin/api/v1/master/communications/tasks`, body).pipe(
      this.loadingService.withLoadingScreen,
      tap(() => this.toggleReload.emit()),
      catchError((res: HttpErrorResponse) => {
        this.toast.error(`Error creating task. ${res.error?.reason || 'Unknown'} `);
        return throwError(`Error creating task. ${res.error?.reason || 'Unknown'} `);
      })
    );
  }

  public updateTaskSettings(id: string, body: CommunicationModel): Observable<unknown> {
    return this.httpClient
      .patch(`${environment.api.baseUrl}/admin/api/v1/master/communications/tasks/${id}/settings`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error updating task settings. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error updating task settings. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public publishTask(id: string, body: CommunicationModel): Observable<TransactionStatus<TransactionStatusMetadata>> {
    return this.httpClient
      .patch<
        TransactionStatus<TransactionStatusMetadata>
      >(`${environment.api.baseUrl}/admin/api/v1/master/communications/tasks/${id}/publish`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error publishing task. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error publishing task. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public archiveTask(id: string, body: CommunicationModel): Observable<unknown> {
    return this.httpClient
      .patch(`${environment.api.baseUrl}/admin/api/v1/master/communications/tasks/${id}/archive`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error archiving task. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error archiving task. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public updateTaskMessage(id: string, body: CommunicationModel): Observable<unknown> {
    return this.httpClient
      .patch(`${environment.api.baseUrl}/admin/api/v1/master/communications/tasks/${id}/message`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error updating task message. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error updating task message. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public updateTasksRuleSet(id: string, body: CommunicationModel): Observable<unknown> {
    body.ruleSet = RuleEngineHelper.ruleSetV2ToDto(body.ruleSet);
    return this.httpClient
      .patch(`${environment.api.baseUrl}/admin/api/v1/master/communications/tasks/${id}/rule-set`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error updating task target group. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error updating task target group. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public deleteTaskLabels(id: string, body: TagModel): Observable<unknown> {
    return this.httpClient
      .request('delete', `${environment.api.baseUrl}/admin/api/v1/master/communications/tasks/${id}/labels`, { body })
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`${res?.error?.message}: ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error deleting labels. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  /* MAIL: */
  public getMail(id: string): Observable<CommunicationModel> {
    return this.httpClient
      .get<CommunicationModel>(`${environment.api.baseUrl}/admin/api/v1/master/communications/mails/${id}`)
      .pipe(
        map(checkResult()),
        retryWhen(pollingStrategy()),
        switchMap(result => this.addRuleSet(result)),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error retrieving mails. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error retrieving mails data. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public createMail(body: CommunicationModel): Observable<unknown> {
    return this.httpClient.post(`${environment.api.baseUrl}/admin/api/v1/master/communications/mails`, body).pipe(
      this.loadingService.withLoadingScreen,
      tap(() => this.toggleReload.emit()),
      catchError((res: HttpErrorResponse) => {
        this.toast.error(`Error creating mail. ${res.error?.reason || 'Unknown'} `);
        return throwError(`Error creating mail. ${res.error?.reason || 'Unknown'} `);
      })
    );
  }

  public updateMailSettings(id: string, body: CommunicationModel): Observable<unknown> {
    return this.httpClient
      .patch(`${environment.api.baseUrl}/admin/api/v1/master/communications/mails/${id}/settings`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error updating mail settings. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error updating mail settings. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public updateMailMessage(id: string, body: CommunicationModel): Observable<unknown> {
    return this.httpClient
      .patch(`${environment.api.baseUrl}/admin/api/v1/master/communications/mails/${id}/message`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error updating mail message. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error updating mail message. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public updateMailRuleSet(id: string, body: CommunicationModel): Observable<unknown> {
    body.ruleSet = RuleEngineHelper.ruleSetV2ToDto(body.ruleSet);
    return this.httpClient
      .patch(`${environment.api.baseUrl}/admin/api/v1/master/communications/mails/${id}/rule-set`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error updating mail target group. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error updating mail target group. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public publishMail(id: string, body: CommunicationModel): Observable<TransactionStatus<TransactionStatusMetadata>> {
    return this.httpClient
      .patch<
        TransactionStatus<TransactionStatusMetadata>
      >(`${environment.api.baseUrl}/admin/api/v1/master/communications/mails/${id}/publish`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error publishing mail. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error publishing mail. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public archiveMail(id: string, body: CommunicationModel): Observable<unknown> {
    return this.httpClient
      .patch(`${environment.api.baseUrl}/admin/api/v1/master/communications/mails/${id}/archive`, body)
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error archiving mail. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error archiving mail. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public deleteMailLabels(id: string, body: TagModel): Observable<unknown> {
    return this.httpClient
      .request('delete', `${environment.api.baseUrl}/admin/api/v1/master/communications/mails/${id}/labels`, { body })
      .pipe(
        this.loadingService.withLoadingScreen,
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`${res?.error?.message}: ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error deleting labels. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }

  public sendTestMail(ruleSet: RuleSetV2, id: string): Observable<void> {
    return this.httpClient
      .post<void>(`${environment.api.baseUrl}/admin/api/v2/master/communications/mails/test`, {
        id,
        ruleSet: RuleEngineHelper.ruleSetV2ToDto(ruleSet)
      })
      .pipe(
        this.loadingService.withLoadingScreen,
        tap(() => this.toast.success('Emails sent')),
        this.httpHelper.handleError('Cannot send test mail')
      );
  }

  public getAllowedMailAddresses(): Observable<AllowedAddress[]> {
    return this.httpClient
      .get<AllowedAddress[]>(`${environment.api.baseUrl}/admin/api/v2/master/communications/mails/allowed-addresses`)
      .pipe(this.httpHelper.handleError('Error retrieving allowed mail-addresses'));
  }

  // DEFAULT MAILS
  public getDefaultMail(): Observable<string> {
    return this.httpClient.get('./assets/html/editor-input.html', { responseType: 'text' });
  }
  public getDefaultMail2(): Observable<string> {
    return this.httpClient.get('./assets/html/editor-input_short.html', { responseType: 'text' });
  }
  public getDefaultMail3(): Observable<string> {
    return this.httpClient.get('./assets/html/editor-input_nologo.html', { responseType: 'text' });
  }
  public getBeforeMailContent(): Observable<string> {
    return this.httpClient.get('./assets/html/before-mail.html', { responseType: 'text' });
  }
  public getAfterMailContent(): Observable<string> {
    return this.httpClient.get('./assets/html/after-mail.html', { responseType: 'text' });
  }

  /* GENERAL: */
  public uploadImage(url: string, image: unknown): Observable<unknown> {
    this.loadingService.present();
    return this.httpClient.put(url, image).pipe(
      catchError((res: HttpErrorResponse) => {
        this.toast.error(`Error uploading image. ${res.error?.reason || 'Unknown'} `);
        return throwError(`Error uploading image. ${res.error?.reason || 'Unknown'} `);
      }),
      finalize(() => this.loadingService.dismiss())
    );
  }

  /* LABELS: */
  public getCommunicationsLabels(name: string): Observable<TagModel[]> {
    return this.httpClient
      .get<TagModel[]>(`${environment.api.baseUrl}/admin/api/v1/master/communications/labels?Query.name=${name}`)
      .pipe(
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error retrieving communication labels. ${res.error?.reason || 'Unknown'} `);
          return throwError(`Error retrieving communication labels data. ${res.error?.reason || 'Unknown'} `);
        })
      );
  }
  public postCommunicationsLabel(body: TagModel): Observable<unknown> {
    return this.httpClient.post(`${environment.api.baseUrl}/admin/api/v1/master/communications/labels`, body).pipe(
      this.loadingService.withLoadingScreen,
      catchError((res: HttpErrorResponse) => {
        this.toast.error(`${res?.error?.message}: ${res.error?.reason || 'Unknown'} `);
        return throwError(`Error creating label. ${res.error?.reason || 'Unknown'} `);
      })
    );
  }

  private addRuleSet(communication: CommunicationModel): Observable<CommunicationModel> {
    if (!communication.ruleSetId) {
      return of(communication);
    }
    return this.ruleSet.getRuleSet(communication.ruleSetId).pipe(
      map(ruleSet => ({ ...communication, ruleSet }) as CommunicationModel),
      catchError(() => of(communication))
    );
  }
}
